import {assert} from 'Asserts';
import {type Table} from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import excess from 'notebook/tables/excess';
import {type EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {type TableOpDiff} from 'notebook/tables/excess/ops/TableOpConstants';
import {persistTable, type TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {saveSelection, setMultiIndices} from 'notebook/tables/store/logic/mutations/selection';
import {preserveDestinationSelectorIdsForFillDown} from 'notebook/tables/store/logic/SelectorPreservationLogic';
import {computeContainsMergedRegion} from 'notebook/tables/store/logic/TableMergedCellLogic';
import {computeHasRealSelection} from 'notebook/tables/store/logic/TableSelectionLogic';
import notifications from 'notifications';

/**
 * Fill down the current selection into the active fill-down box.
 *
 * Checks fill-down locking (surfacing a warning notification and aborting when locked), reads the
 * source selection (`expandedBox`) and destination (`fillDownBox`) from the models, checkpoints the
 * current selection for undo via `saveSelection`, extrapolates via the pure `_fillDownTransform`,
 * persists the change, and updates the selection to the filled region. No-op when there is no active
 * fill-down box or the selection is not fully within the table.
 */
export function performFillDown(params: TableMutationParams): void {
  const model = params.getTableModel();
  const {isLocked, lockedWarningMessage, box: fillDownBox} = model.fillDown;

  // Surface the lock warning and abort before mutating, matching the original
  // `TableEditable.isFillDownBoxLockedWithEditWarn`.
  if (isLocked) {
    if (lockedWarningMessage) {
      notifications.warn(lockedWarningMessage, {
        id: 'TableEditable.isLockedWithEditWarn',
      });
    }
    return;
  }

  const {expandedBox} = model.selection;
  // Matches the original `TableEditable._multiSelectionBox`, which hard-asserts
  // a non-null selection box in all environments.
  assert(expandedBox != null, 'Expected to have a selection');

  const {table, disableFormulas, computedTable, selectorsLink, isProtected} = params.getTableProps();

  // `disableFormulas` is optional and defaults to disabled being false,
  // matching the default applied to the `TableEditable` component props.
  const result = _fillDownTransform(table, computedTable, expandedBox, fillDownBox, disableFormulas ?? false);
  if (result == null) {
    return;
  }

  // Snapshot the current selection into undo history before mutating.
  saveSelection(params);

  const {box, tableOp} = result;
  const newTable = isProtected
    ? preserveDestinationSelectorIdsForFillDown(table, result.table, box)
    : result.table;

  persistTable(params, {table: newTable, tableOp}, selectorsLink.value);

  setMultiIndices(params, {
    start: [box.minI, box.minJ],
    end: [box.maxI, box.maxJ],
    text: newTable.table[box.minI][box.minJ].text,
  });
}

/**
 * Fill down the current selection through the following blank rows, triggered by double-clicking the
 * fill handle.
 *
 * Reads the source selection (`expandedBox`) and the double-click destination (`fillDown.doubleClickBox`)
 * from the models, extrapolates via the pure `_doubleClickFillDownTransform`, persists the change, and
 * updates the selection to the filled region. No-op when the selection contains a merged region.
 */
export function performDoubleClickFillDown(params: TableMutationParams): void {
  const model = params.getTableModel();

  const {expandedBox} = model.selection;
  assert(expandedBox != null, 'Expected to have a selection');

  const {doubleClickBox} = model.fillDown;
  const {table, disableFormulas, computedTable, selectorsLink, isProtected} = params.getTableProps();

  // `disableFormulas` is optional and defaults to disabled being false,
  // matching the default applied to the `TableEditable` component props.
  const result = _doubleClickFillDownTransform(
    table,
    computedTable,
    expandedBox,
    doubleClickBox,
    disableFormulas ?? false
  );

  // No-op when the selection contains a merged region.
  if (result == null) {
    return;
  }

  const {box, tableOp} = result;
  const newTable = isProtected
    ? preserveDestinationSelectorIdsForFillDown(table, result.table, box)
    : result.table;

  persistTable(params, {table: newTable, tableOp}, selectorsLink.value);

  setMultiIndices(params, {
    start: [box.minI, box.minJ],
    end: [box.maxI, box.maxJ],
    text: newTable.table[box.minI][box.minJ].text,
  });
}

/** The result of a fill-down mutation. */
export interface FillDownResult {
  /** The new table with the filled-down cells applied. */
  table: Table;
  /** The region that was filled into, as {minI, maxI, minJ, maxJ}. */
  box: MultiSelectionBox;
  /** The table op describing the change, for persistence / undo. */
  tableOp: TableOpDiff;
}

/**
 * Fill-down the `srcBox` region across the `dstBox` region of the table.
 *
 * @param table - The table to modify.
 * @param computedTable - The evaluated cells of the table (results of formulas).
 * @param srcBox - The source selection to tile / extrapolate from.
 * @param dstBox - The destination region to fill into. Must match `srcBox` in at
 *   least one dimension.
 * @param disableFormulas - Whether formulas are disabled for this table.
 * @returns The fill-down result, or `null` when there is no destination box or
 *   when `srcBox` is not fully within the table (e.g. it extends into ghost cells
 *   or references rows that no longer exist).
 */
export function _fillDownTransform(
  table: Table,
  computedTable: Array<Array<EvaluatedCell>>,
  srcBox: MultiSelectionBox,
  dstBox: MultiSelectionBox | null | undefined,
  disableFormulas: boolean
): FillDownResult | null {
  if (dstBox == null || !computeHasRealSelection(srcBox, table)) {
    return null;
  }
  return excess.ops.fillDown(table, computedTable, srcBox, dstBox, disableFormulas);
}

/**
 * Fill-down triggered by double-clicking the fill handle.
 *
 * Double-click fill-down extends the `srcBox` selection down through the
 * following blank rows (the `dstBox`, precomputed as the double-click fill-down
 * box). It is a no-op when the selection contains any merged region.
 *
 * @param table - The table to modify.
 * @param computedTable - The evaluated cells of the table (results of formulas).
 * @param srcBox - The source selection to tile / extrapolate from.
 * @param dstBox - The destination region to fill into (the double-click fill-down
 *   box). Must match `srcBox` in the column dimension.
 * @param disableFormulas - Whether formulas are disabled for this table.
 * @returns The fill-down result, or `null` when there is no destination box or
 *   when the selection contains a merged region (no fill-down is performed).
 */
export function _doubleClickFillDownTransform(
  table: Table,
  computedTable: Array<Array<EvaluatedCell>>,
  srcBox: MultiSelectionBox,
  dstBox: MultiSelectionBox | null | undefined,
  disableFormulas: boolean
): FillDownResult | null {
  // Double-click fill down does nothing if there is no destination box or if the
  // selected cells contain any merged cells.
  if (dstBox == null) {
    return null;
  }
  if (computeContainsMergedRegion(computedTable, srcBox)) {
    return null;
  }
  return _fillDownTransform(table, computedTable, srcBox, dstBox, disableFormulas);
}
